import { useState, useEffect, useRef } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'

/**
 * Kapselt die Registrierung des Service Workers und stellt
 * Update-Status + manuellen Update-Trigger bereit.
 *
 * - `needsUpdate` – true wenn ein neues SW im Hintergrund bereit ist
 * - `updateNow`  – aktiviert das neue SW (→ App wird neu geladen)
 * - `dismiss`    – blendet den Banner aus (ohne Update)
 *
 * registerType ist 'prompt' (siehe vite.config.ts) — der Nutzer entscheidet über
 * den UpdateBanner, wann aktualisiert wird. Ein controllerchange-Guard verhindert
 * einen automatischen Reload-Loop durch ältere Service Worker (skipWaiting).
 */
export function useServiceWorker() {
  const [dismissed, setDismissed] = useState(false)
  const [externalUpdate, setExternalUpdate] = useState(false)
  const reloadAllowed = useRef(false)

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, r) {
      // Alle 60 Minuten auf Updates prüfen
      if (r) {
        setInterval(() => r.update(), 60 * 60 * 1000)
      }
    },
    onRegisterError(error) {
      console.warn('[SW] Registrierung fehlgeschlagen:', error)
    },
  })

  // ── Guard: controllerchange darf NICHT automatisch reloaden ──────────────
  useEffect(() => {
    const sw = navigator.serviceWorker
    if (!sw) return

    const onControllerChange = () => {
      if (reloadAllowed.current) {
        // Nur reloaden wenn der Nutzer explizit auf "Jetzt aktualisieren" geklickt hat
        window.location.reload()
        return
      }
      // Externer SW-Wechsel (z.B. alter SW mit skipWaiting) → Banner zeigen statt reload
      console.info('[SW] Controller gewechselt — Update-Banner wird angezeigt')
      setExternalUpdate(true)
      setNeedRefresh(true)
    }

    sw.addEventListener('controllerchange', onControllerChange)
    return () => sw.removeEventListener('controllerchange', onControllerChange)
  }, [setNeedRefresh])

  // dismissed zurücksetzen wenn ein neues Update kommt
  useEffect(() => {
    if (needRefresh || externalUpdate) setDismissed(false)
  }, [needRefresh, externalUpdate])

  const handleUpdate = () => {
    // Erlaubt dem controllerchange-Handler den Reload
    reloadAllowed.current = true
    updateServiceWorker(true)
  }

  return {
    needsUpdate: (needRefresh || externalUpdate) && !dismissed,
    updateNow: handleUpdate,
    dismiss: () => setDismissed(true),
  }
}
